/**
 * WebVitalsObserver — Captura métricas de rendimiento (Web Vitals)
 * Mide: LCP, FID, CLS, INP, TTFB, FCP, DCL, page load time
 * Envía como evento único 'page_vitals' con todas las métricas
 */

export class WebVitalsObserver {
    private static vitals: any = {};
    private static telemetry: any = null;
    private static pageType: string = 'unknown';
    private static metricsSent: boolean = false;

    /**
     * Captura Web Vitals y envía evento a telemetría
     * @param telemetry Instancia de TelemetryClient
     * @param pageType 'home' | 'landing'
     */
    static captureHomePageVitals(telemetry: any, pageType: string = 'home'): void {
        if (!telemetry) {
            console.warn('[WebVitals] TelemetryClient not available');
            return;
        }

        this.telemetry = telemetry;
        this.pageType = pageType;
        this.vitals = {};
        this.metricsSent = false;

        // Obtener timing de navigation
        this.captureNavigationTiming();

        // Observar Paint timing (FCP)
        this.observePaintTiming();

        // Observar Largest Contentful Paint (LCP)
        this.observeLCP();

        // Observar Cumulative Layout Shift (CLS)
        this.observeCLS();

        // Observar First Input Delay (FID) y Interaction to Next Paint (INP)
        this.observeInputDelay();

        // Enviar evento después de que todas las métricas estén listas
        this.sendMetricsAfterDelay();
    }

    /**
     * Captura métricas del objeto performance.timing
     */
    private static captureNavigationTiming(): void {
        if (!window.performance || !window.performance.timing) {
            return;
        }

        const timing = performance.timing;
        const navigationStart = timing.navigationStart;

        // TTFB (Time to First Byte) — desde navigationStart hasta responseStart
        if (timing.responseStart > navigationStart) {
            this.vitals.ttfb_ms = timing.responseStart - navigationStart;
        }

        // DCL (DOMContentLoaded) — tiempo hasta que se dispara el evento
        if (timing.domContentLoadedEventEnd > navigationStart) {
            this.vitals.dcl_ms = timing.domContentLoadedEventEnd - navigationStart;
        }

        // Total page load time — desde navigationStart hasta loadEventEnd
        if (timing.loadEventEnd > navigationStart) {
            this.vitals.page_load_ms = timing.loadEventEnd - navigationStart;
        }

        // FCP (First Contentful Paint) — intentar obtener de PerformanceObserver
        if (timing.responseStart > navigationStart) {
            // Aproximación: FCP es generalmente ~200-400ms después de TTFB
            // Se refina con observePaintTiming()
            this.vitals.fcp_ms = timing.responseStart - navigationStart + 300;
        }
    }

    /**
     * Observa paint events (FCP - First Contentful Paint)
     */
    private static observePaintTiming(): void {
        if (!(window as any).PerformanceObserver) {
            return;
        }

        try {
            const observer = new (window as any).PerformanceObserver((list: any) => {
                const entries = list.getEntries();
                for (const entry of entries) {
                    if (entry.name === 'first-contentful-paint') {
                        this.vitals.fcp_ms = Math.round(entry.startTime);
                    }
                }
            });

            observer.observe({ entryTypes: ['paint'] });

            // Auto-cleanup después de 10 segundos
            setTimeout(() => observer.disconnect(), 10000);
        } catch (e) {
            console.warn('[WebVitals] PerformanceObserver paint error:', e);
        }
    }

    /**
     * Observa Largest Contentful Paint (LCP)
     */
    private static observeLCP(): void {
        if (!(window as any).PerformanceObserver) {
            return;
        }

        try {
            const observer = new (window as any).PerformanceObserver((list: any) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];
                this.vitals.lcp_ms = Math.round(lastEntry.renderTime || lastEntry.loadTime);
            });

            observer.observe({ entryTypes: ['largest-contentful-paint'] });

            // LCP es valido hasta que el usuario interactúa
            const handleInput = () => {
                observer.disconnect();
                document.removeEventListener('click', handleInput);
                document.removeEventListener('keydown', handleInput);
            };

            document.addEventListener('click', handleInput);
            document.addEventListener('keydown', handleInput);

            // Timeout de 15 segundos máximo
            setTimeout(() => observer.disconnect(), 15000);
        } catch (e) {
            console.warn('[WebVitals] PerformanceObserver LCP error:', e);
        }
    }

    /**
     * Observa Cumulative Layout Shift (CLS)
     * - cls_score / cls_target / cls_shift_ms van en page_vitals (lo acumulado hasta load + 1 s).
     * - Al ocultar la página se manda 'cls_final' con el CLS de toda la visita, calculado como Google
     *   (peor ventana de sesión: shifts a <1 s entre sí, ventana de máx. 5 s) y el elemento que más se movió.
     *   Así se ven los saltos que llegan después de la carga (p. ej. al primer scroll).
     */
    private static observeCLS(): void {
        if (!(window as any).PerformanceObserver?.supportedEntryTypes?.includes('layout-shift')) return;

        let total = 0, ventana = 0, inicioVentana = 0, ultimo = 0, peorVentana = 0;
        let mayor: any = null;
        let enviado = false;

        try {
            new PerformanceObserver((list: any) => {
                for (const entry of list.getEntries()) {
                    if (entry.hadRecentInput) continue; // shifts tras clic/tecla no cuentan (igual que Google)
                    total += entry.value;
                    if (entry.startTime - ultimo > 1000 || entry.startTime - inicioVentana > 5000) {
                        ventana = 0;
                        inicioVentana = entry.startTime;
                    }
                    ventana += entry.value;
                    ultimo = entry.startTime;
                    peorVentana = Math.max(peorVentana, ventana);
                    if (!mayor || entry.value > mayor.value) mayor = { value: entry.value, ms: Math.round(entry.startTime), target: this.shiftTarget(entry) };
                }
                this.vitals.cls_score = Math.round(total * 100) / 100; // 2 decimales
                if (mayor) {
                    this.vitals.cls_target = mayor.target;
                    this.vitals.cls_shift_ms = mayor.ms;
                }
            }).observe({ type: 'layout-shift', buffered: true });
        } catch (e) {
            console.warn('[WebVitals] PerformanceObserver CLS error:', e);
            return;
        }

        document.addEventListener('visibilitychange', () => {
            if (!document.hidden || enviado) return;
            enviado = true;
            this.telemetry?.send('cls_final', {
                page_type: this.pageType,
                page_url: window.location.pathname,
                cls: Math.round(peorVentana * 1000) / 1000,
                cls_target: mayor?.target ?? null,
                cls_shift_ms: mayor?.ms ?? null,
                cls_shift_value: mayor ? Math.round(mayor.value * 1000) / 1000 : null,
                viewport_width: window.innerWidth,
            });
        });
    }

    /** Selector corto del nodo que más se movió en un layout-shift: "padre > tag#id.clase1.clase2" */
    private static shiftTarget(entry: any): string | null {
        const src = (entry.sources || []).reduce((a: any, b: any) =>
            !a || b.currentRect.width * b.currentRect.height > a.currentRect.width * a.currentRect.height ? b : a, null);
        let n = src?.node;
        if (!n) return null;
        if (n.nodeType !== 1) n = n.parentElement; // nodo de texto → su elemento
        const sel = (el: Element) => el.tagName.toLowerCase() + (el.id ? '#' + el.id : '')
            + (typeof el.className === 'string' && el.className.trim() ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.') : '');
        return ((n.parentElement ? sel(n.parentElement) + ' > ' : '') + sel(n)).slice(0, 150);
    }

    /**
     * Observa First Input Delay (FID) e Interaction to Next Paint (INP)
     */
    private static observeInputDelay(): void {
        if (!(window as any).PerformanceObserver) {
            return;
        }

        try {
            const observer = new (window as any).PerformanceObserver((list: any) => {
                const entries = list.getEntries();
                for (const entry of entries) {
                    if (entry.name === 'first-input') {
                        this.vitals.fid_ms = Math.round(entry.processingStart - entry.startTime);
                        this.vitals.has_fid = true;
                    }
                    if (entry.name === 'interaction') {
                        const duration = entry.duration || 0;
                        this.vitals.inp_ms = Math.round(duration);
                        this.vitals.has_inp = true;
                    }
                }
            });

            const supported = (PerformanceObserver as any).supportedEntryTypes || [];
            const entryTypes = ['first-input', 'interaction'].filter((t: string) => supported.includes(t));
            if (!entryTypes.length) return;
            observer.observe({ entryTypes });

            // Observar hasta que la página sea invisible o pasado 1 minuto
            const handleVisibility = () => {
                if (document.hidden) {
                    observer.disconnect();
                    document.removeEventListener('visibilitychange', handleVisibility);
                }
            };

            document.addEventListener('visibilitychange', handleVisibility);

            setTimeout(() => {
                observer.disconnect();
                document.removeEventListener('visibilitychange', handleVisibility);
            }, 60000);
        } catch (e) {
            console.warn('[WebVitals] PerformanceObserver input error:', e);
        }
    }

    /**
     * Envía métricas después de que hayan sido capturadas (solo una vez)
     */
    private static sendMetricsAfterDelay(): void {
        // Enviar cuando el documento está completamente cargado + 1 segundo extra para capturar todas las métricas
        // LCP, CLS y FID generalmente están listos para ese momento
        const sendOnce = () => {
            if (!this.metricsSent) {
                this.metricsSent = true;
                setTimeout(() => this.sendMetrics(), 1000);
            }
        };

        // Si el documento ya está listo, enviar inmediatamente
        if (document.readyState === 'complete') {
            sendOnce();
        } else {
            // Si no, esperar a que esté listo
            window.addEventListener('load', sendOnce, { once: true });
        }
    }

    /**
     * Envía el evento de page_vitals
     */
    private static sendMetrics(): void {
        // Solo enviar si hay al menos algunas métricas
        if (Object.keys(this.vitals).length === 0) {
            return;
        }

        // Agregar contexto adicional
        const payload: any = {
            page_type: this.pageType,
            page_url: window.location.pathname,
            ...this.vitals,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
        };

        // Agregar tipo de conexión si está disponible
        if ((navigator as any).connection) {
            payload.connection_type = (navigator as any).connection.effectiveType || 'unknown';
            payload.connection_rtt_ms = (navigator as any).connection.rtt || null;
            payload.connection_downlink_mbps = (navigator as any).connection.downlink || null;
        }

        // Agregar información de memoria si está disponible
        if ((performance as any).memory) {
            payload.memory_used_mb = Math.round((performance as any).memory.usedJSHeapSize / 1048576);
            payload.memory_limit_mb = Math.round((performance as any).memory.jsHeapSizeLimit / 1048576);
        }

        this.telemetry?.send('page_vitals', payload);
    }
}

// Registrar en window para acceso global
(window as any).WebVitalsObserver = WebVitalsObserver;
